import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import { Message, apiService, QuestionRequest, QuestionOptions } from '../services/api';
import { escapeCurrencyDollars } from '../utils/markdown';
import { chatErrorMessage } from '../utils/chatError';

// Helper function to process mathematical content
const processMathContent = (content: string): string => {
  let processed = content;
  
  // Pattern 1: Handle expressions in square brackets with LaTeX
  processed = processed.replace(/(?<!\$)\[\s*([^[\]]*\\text\{[^}]*\}[^[\]]*)\s*\]/g, '$[$1$]$');
  
  // Pattern 2: Handle standalone \text{} expressions
  processed = processed.replace(/(?<!\$)\\text\{([^}]*)\}/g, '$\\text{$1}$');
  
  // Pattern 3: Handle mathematical expressions with percentages and operators
  processed = processed.replace(/(?<!\$)(\d+\.?\d*%\s*\\text\{[^}]+\})/g, '$$$1$$');
  
  // Pattern 4: Process math content within HTML table cells
  processed = processed.replace(/(<t[dh][^>]*>)([^<]*\\text\{[^}]*\}[^<]*)(<\/t[dh]>)/g, (match, openTag, cellContent, closeTag) => {
    const processedCell = cellContent
      .replace(/(?<!\$)\\text\{([^}]*)\}/g, '$\\text{$1}$')
      .replace(/(?<!\$)(\d+\.?\d*%)/g, '$$$1$$');
    return openTag + processedCell + closeTag;
  });
  
  // Pattern 5: Handle percentage signs in HTML tables that might need math formatting
  processed = processed.replace(/(<t[dh][^>]*>)([^<]*\d+\.?\d*%[^<]*)(<\/t[dh]>)/g, (match, openTag, cellContent, closeTag) => {
    if (!cellContent.includes('$')) {
      const processedCell = cellContent.replace(/(\d+\.?\d*%)/g, '$$$1$$');
      return openTag + processedCell + closeTag;
    }
    return match;
  });
  
  // Pattern 6: Handle percentages in markdown table cells (between | separators)
  processed = processed.replace(/(\|[^|\n]*?)(\d+\.?\d*)(%[^|\n]*?\|)/g, (match, before, number, after) => {
    if (!match.includes('$')) {
      return before + '$' + number + '\\%$' + after.replace('%', '');
    }
    return match;
  });
  
  return processed;
};

// Seed prompts for the empty state — one per capability the client tested, drawn from
// gate-passing eval questions across both answer lanes (PE/VC statistics + policy documents).
const EXAMPLE_PROMPTS: { label: string; prompt: string }[] = [
  {
    label: 'Top countries by PE investment',
    prompt: 'What were the top 5 European countries by private equity investment value in 2024?',
  },
  {
    label: 'Venture capital by country',
    prompt: 'How much venture capital was invested into companies located in the Netherlands in 2023 and 2024?',
  },
  {
    label: 'European fundraising',
    prompt: 'How much did private equity funds raise in Europe in 2024?',
  },
  {
    label: "Invest Europe's SFDR position",
    prompt: "What is Invest Europe's position on SFDR?",
  },
  {
    label: 'Compare SFDR and CSRD',
    prompt: 'Compare the SFDR and CSRD reporting requirements.',
  },
  {
    label: 'What is private equity?',
    prompt: 'What is private equity?',
  },
];

interface ChatInterfaceProps {
  selectedProject: string | null;
  activeConversation: string | null;
  executionMode: 'basic' | 'extended';
  personalityMode: 'simple' | 'professional' | 'teacher';
  advancedOptions: Partial<QuestionOptions>;
  historicalMessages?: Message[];
  userId: string;
  onActiveConversationChange: (conversationId: string) => void;
  onLoadingConversationsChange?: (loadingConversations: Set<string>) => void;
  onConversationCreated?: () => void; // Trigger refresh without changing active conversation
  onNewChat?: () => void; // Start a fresh conversation (used by empty-state example prompts)
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ 
  selectedProject, 
  activeConversation,
  executionMode,
  personalityMode,
  advancedOptions,
  historicalMessages,
  userId,
  onActiveConversationChange,
  onLoadingConversationsChange,
  onConversationCreated,
  onNewChat
}) => {
  // Store messages per conversation ID
  const [messagesMap, setMessagesMap] = useState<Record<string, Message[]>>({});
  const [lastResponse, setLastResponse] = useState<any>(null);
  const [inputValue, setInputValue] = useState('');
  // Store loading state per conversation ID
  const [loadingConversations, setLoadingConversations] = useState<Set<string>>(new Set());

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingRequestConversationRef = useRef<string | null>(null);

  // Get messages for the currently active conversation
  const messages = activeConversation ? (messagesMap[activeConversation] || []) : [];
  const isLoading = activeConversation ? loadingConversations.has(activeConversation) : false;

  // Auto-scroll to bottom when messages are loaded or conversation changes
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  }, [messages.length, activeConversation]); // Trigger when messages are loaded or conversation changes

  // Notify parent when loading conversations change
  useEffect(() => {
    if (onLoadingConversationsChange) {
      onLoadingConversationsChange(loadingConversations);
    }
  }, [loadingConversations, onLoadingConversationsChange]);

  // Clear messages when switching conversations or load historical messages
  useEffect(() => {
    if (activeConversation) {
      if (historicalMessages && historicalMessages.length > 0) {
        // Strategy: When switching to a conversation, prefer backend as source of truth
        // Only keep local messages that are not yet in the backend (based on content)
        setMessagesMap(prev => {
          const existingMessages = prev[activeConversation] || [];
          
          // Create a map of backend message content for quick lookup
          const backendContentSet = new Set(
            historicalMessages.map(m => `${m.role}:${m.content.substring(0, 100)}`) // Use first 100 chars to handle very long messages
          );
          
          // Find the latest backend timestamp
          const latestBackendTimestamp = Math.max(...historicalMessages.map(m => m.timestamp));
          
          // Keep only local messages that:
          // 1. Are not in backend (based on content signature)
          // 2. AND are newer than any backend message (likely user questions waiting for response)
          const localOnlyMessages = existingMessages.filter(m => {
            const signature = `${m.role}:${m.content.substring(0, 100)}`;
            const notInBackend = !backendContentSet.has(signature);
            const isNewer = m.timestamp > latestBackendTimestamp;
            return notInBackend && isNewer;
          });
          
          console.log(`[ChatInterface] Merging: ${historicalMessages.length} backend + ${localOnlyMessages.length} local-only messages`);
          
          // Combine: backend messages (source of truth) + truly local-only messages
          const combined = [...historicalMessages, ...localOnlyMessages].sort((a, b) => a.timestamp - b.timestamp);
          
          return {
            ...prev,
            [activeConversation]: combined
          };
        });
      } else if (!messagesMap[activeConversation]) {
        // Initialize empty array for new conversations (only if not already present)
        setMessagesMap(prev => ({
          ...prev,
          [activeConversation]: []
        }));
      }
    }
  }, [activeConversation, historicalMessages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputValue.trim() || !selectedProject || isLoading) {
      return;
    }

    const userMessage: Message = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: Date.now(),
    };

    // Capture the conversation ID at the time of asking the question
    const requestConversationId = activeConversation || 'new';
    pendingRequestConversationRef.current = requestConversationId;
    
    console.log(`[ChatInterface] Asking question in conversation: ${requestConversationId}`);
    
    // Add user message to the specific conversation
    setMessagesMap(prev => ({
      ...prev,
      [requestConversationId]: [...(prev[requestConversationId] || []), userMessage]
    }));
    
    setInputValue('');
    
    // Set loading state for this specific conversation
    setLoadingConversations(prev => {
      const newSet = new Set(prev).add(requestConversationId);
      console.log(`[ChatInterface] Setting loading for conversation: ${requestConversationId}, all loading:`, Array.from(newSet));
      return newSet;
    });

    try {
      const options: QuestionOptions = {
        mode: executionMode,
        personality_mode: personalityMode,
        ...advancedOptions
      };
      
      const request: QuestionRequest = {
        selectedProject,
        question: userMessage.content,
        options,
        session_id: requestConversationId || undefined
        // user_id removed - backend now uses authenticated user from session
      };

      console.log(`[ChatInterface] Sending request with session_id: ${requestConversationId || 'undefined (new conversation)'}`);
      const response = await apiService.askQuestion(request);
      console.log(`[ChatInterface] Received response with conversation_id: ${response.conversation_id || 'undefined'}`);
      
      // Determine the conversation ID to store the response in
      const conversationIdForResponse = response.conversation_id || requestConversationId;
      
      // Update active conversation if backend returned a conversation_id
      if (response.conversation_id) {
        const conversationChanged = response.conversation_id !== requestConversationId;
        
        // If user is still on the original conversation (or new conversation page), update to the new ID
        if (activeConversation === requestConversationId || requestConversationId === 'new') {
          onActiveConversationChange(response.conversation_id);
          console.log(`✓ Active conversation updated to: ${response.conversation_id}`);
          
          // If we had messages under 'new' or a different temp ID, migrate them to the real conversation ID
          if (conversationChanged && (requestConversationId === 'new' || requestConversationId !== response.conversation_id)) {
            setMessagesMap(prev => {
              const oldMessages = prev[requestConversationId] || [];
              const updated = { ...prev };
              if (requestConversationId !== response.conversation_id) {
                delete updated[requestConversationId];
              }
              updated[response.conversation_id!] = oldMessages;
              return updated;
            });
          }
        } else {
          // User switched away - just trigger refresh without changing active conversation
          if (onConversationCreated) {
            onConversationCreated();
            console.log(`✓ New conversation created (${response.conversation_id}) - triggering refresh`);
          }
        }
      }
      
      // Handle response - answer field already contains formatted clarification if needed
      let messageContent = response.answer || 'No response received';
      
      // Log clarification for debugging
      if (response.needs_clarification) {
        console.log('[ChatInterface] Clarification response:', response.answer);
        console.log('[ChatInterface] Clarification info:', response.clarification_info);
        // Add emoji prefix for clarification responses
        if (messageContent && messageContent !== 'No response received') {
          messageContent = `${messageContent}`;
        }
      }
      
      const assistantMessage: Message = {
        role: 'assistant',
        content: messageContent,
        timestamp: Date.now(),
      };

      // Only add the response to messagesMap if we're still viewing this conversation
      // If user switched away, the response will be loaded from backend when they switch back
      if (activeConversation === conversationIdForResponse || activeConversation === requestConversationId) {
        setMessagesMap(prev => ({
          ...prev,
          [conversationIdForResponse]: [...(prev[conversationIdForResponse] || []), assistantMessage]
        }));
      }
      
      // Store metadata only if we're still viewing this conversation
      if (activeConversation === conversationIdForResponse || activeConversation === requestConversationId) {
        setLastResponse(response);
      }
      
      console.log(`✓ Response added to conversation: ${conversationIdForResponse}`);
    } catch (error) {
      console.error('Error asking question:', error);
      
      // Show more specific error information
      const errorMessage = chatErrorMessage(error);
      
      if (error instanceof Error) {
        console.log('Error details:', error.message);
      }
      
      const errorMessageObj: Message = {
        role: 'assistant',
        content: errorMessage,
        timestamp: Date.now(),
      };

      // Add error message to the conversation where the request originated
      setMessagesMap(prev => ({
        ...prev,
        [requestConversationId]: [...(prev[requestConversationId] || []), errorMessageObj]
      }));
    } finally {
      // Clear loading state for the conversation that made the request
      console.log(`[ChatInterface] Clearing loading state for conversation: ${requestConversationId}`);
      setLoadingConversations(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestConversationId);
        console.log(`[ChatInterface] Loading conversations after clear:`, Array.from(newSet));
        return newSet;
      });
      pendingRequestConversationRef.current = null;
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const clearChat = () => {
    if (activeConversation) {
      setMessagesMap(prev => ({
        ...prev,
        [activeConversation]: []
      }));
    }
  };

  // Clicking an example prompt fills the composer (starting a chat first if needed) and
  // focuses it, so the user reviews and sends through the normal path. We deliberately do
  // NOT auto-submit: handleSubmit closes over activeConversation, which is still null on the
  // same tick a conversation is created, so the response-display guards would drop the answer.
  const handleExampleClick = (prompt: string) => {
    if (!activeConversation && onNewChat) {
      onNewChat();
    }
    setInputValue(prompt);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  // Auto-resize textarea and trigger re-render for button positioning
  const [textareaHeight, setTextareaHeight] = useState(48);
  const MAX_TEXTAREA_HEIGHT = 200; // Max height before scrolling kicks in

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const newHeight = Math.min(MAX_TEXTAREA_HEIGHT, Math.max(48, scrollHeight));
      textareaRef.current.style.height = `${newHeight}px`;
      setTextareaHeight(newHeight);
    }
  }, [inputValue]);

  return (
    <div className="flex flex-col h-full bg-ie-pale relative">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-ie-rule pl-28 pr-3 min-h-16 py-2 md:pl-6 md:pr-6 md:py-3 flex justify-between items-center flex-shrink-0">
        <div className="flex items-center space-x-3">
          <div className="hidden sm:flex w-8 h-8 bg-ie-navy rounded-full items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h1 className="font-condensed text-lg sm:text-xl font-semibold text-ie-navy leading-tight">
              Research assistant
            </h1>
            <p className="hidden sm:block text-xs text-ie-slate">Answers grounded in Invest Europe data</p>
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={clearChat}
            className="h-9 px-3 sm:h-10 sm:px-5 whitespace-nowrap font-condensed text-sm font-medium uppercase tracking-wide text-ie-navy border border-ie-navy rounded-pill hover:bg-ie-navy hover:text-white transition-colors duration-300"
          >
            Clear Chat
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.length === 0 && selectedProject && (
            <div className="py-10 sm:py-14">
              <div className="w-14 h-14 bg-ie-navy rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-ie-navy mb-3 leading-tight">Invest Europe research assistant</h2>
              <p className="text-ie-slate max-w-xl text-base leading-relaxed">
                Ask about European private equity &amp; venture capital statistics — fundraising,
                investment and divestment by country, year and sector — or about Invest Europe's
                policy positions and published reports. Answers are grounded in Invest Europe's
                own data and documents.
              </p>
              <div className="mt-8 max-w-2xl">
                <p className="font-condensed text-lg font-semibold text-ie-navy mb-2">Try one of these questions</p>
                <div className="border-t border-ie-rule">
                  {EXAMPLE_PROMPTS.map((ex) => (
                    <button
                      key={ex.label}
                      type="button"
                      onClick={() => handleExampleClick(ex.prompt)}
                      className="group block w-full text-left py-3 border-b border-ie-rule transition-colors duration-200"
                    >
                      <span className="block font-condensed text-lg font-semibold text-ie-navy decoration-ie-cyan decoration-2 underline-offset-4 group-hover:underline">{ex.label}</span>
                      <span className="block text-sm text-ie-slate mt-0.5 line-clamp-2">{ex.prompt}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-6 px-3 py-1 bg-ie-mist rounded-full inline-block">
                <p className="text-xs text-ie-navy font-medium">Project: {selectedProject}</p>
              </div>
            </div>
          )}
          
          {!selectedProject && (
            <div className="py-12">
              <div className="w-14 h-14 bg-ie-fog rounded-full flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
              </div>
              <h2 className="font-display text-3xl font-bold text-ie-navy mb-3">Choose a project</h2>
              <p className="text-ie-slate max-w-md text-base leading-relaxed">
                Select a project from the dropdown above to start analyzing your documents with AI assistance.
              </p>
            </div>
          )}

          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              } mb-4`}
            >
              <div
                className={`${
                  message.role === 'user'
                    ? 'max-w-xl px-5 py-3 rounded-lg rounded-br-none bg-ie-navy text-white'
                    : 'w-full max-w-[42rem] px-4 py-4 sm:px-6 sm:py-5 bg-white border border-ie-rule text-ie-navy rounded-tl-sheet rounded-tr-lg rounded-b-lg'
                }`}
              >
                {message.role === 'assistant' ? (
                  <div className="prose prose-ie max-w-none overflow-x-auto">
                    <ReactMarkdown 
                      allowedElements={undefined}
                      disallowedElements={[]}
                      skipHtml={false}
                      remarkPlugins={[remarkGfm, remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                    >
                      {processMathContent(escapeCurrencyDollars(message.content))}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-base whitespace-pre-wrap">{message.content}</p>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start mb-4">
              <div role="status" className="px-5 py-3 rounded-lg bg-white border border-ie-rule">
                <div className="flex items-center space-x-3">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-ie-cyan rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-ie-cyan rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-ie-cyan rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm font-medium text-ie-slate">
                    {executionMode === 'extended' ? 'Working on a thorough answer...' : 'Working on your answer...'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Response Metadata */}
          {lastResponse && lastResponse.orchestrator_metadata && messages.length > 0 && (
            <div className="mt-4 p-4 bg-ie-pale rounded-lg border border-ie-rule">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-ie-navy flex items-center space-x-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12a1 1 0 102 0V9a1 1 0 10-2 0v3zm1-8a1 1 0 100 2 1 1 0 000-2z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                  </svg>
                  <span>Response Analysis</span>
                </h4>
                <div className="flex items-center space-x-4 text-xs text-ie-slate">
                  <span>Mode: <strong className="text-ie-navy">{lastResponse.execution_mode || 'basic'}</strong></span>
                  <span>Style: <strong className="text-ie-navy">{lastResponse.personality_mode || 'professional'}</strong></span>
                  {lastResponse.confidence && (
                    <span>Confidence: <strong className={`${lastResponse.confidence > 0.7 ? 'text-emerald-700' : lastResponse.confidence > 0.5 ? 'text-ie-amber' : 'text-ie-alert'}`}>
                      {(lastResponse.confidence * 100).toFixed(0)}%
                    </strong></span>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                {/* Execution Summary */}
                {lastResponse.orchestrator_metadata.execution_summary && (
                  <div className="bg-white p-3 rounded border">
                    <h5 className="font-medium text-ie-navy mb-2">Agent Summary</h5>
                    <div className="space-y-1 text-ie-slate">
                      <div>Queried: <span className="font-medium">{lastResponse.orchestrator_metadata.execution_summary.total_agents_queried}</span></div>
                      <div>Successful: <span className="font-medium text-emerald-700">{lastResponse.orchestrator_metadata.execution_summary.successful_agents}</span></div>
                      {lastResponse.orchestrator_metadata.execution_summary.failed_agents > 0 && (
                        <div>Failed: <span className="font-medium text-ie-alert">{lastResponse.orchestrator_metadata.execution_summary.failed_agents}</span></div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Query Classification */}
                {lastResponse.orchestrator_metadata.query_classification && (
                  <div className="bg-white p-3 rounded border">
                    <h5 className="font-medium text-ie-navy mb-2">Query Analysis</h5>
                    <div className="space-y-1 text-ie-slate">
                      {lastResponse.orchestrator_metadata.query_classification.complexity && (
                        <div>Complexity: <span className={`font-medium ${
                          lastResponse.orchestrator_metadata.query_classification.complexity === 'SIMPLE' ? 'text-emerald-700' :
                          lastResponse.orchestrator_metadata.query_classification.complexity === 'COMPLEX' ? 'text-ie-amber' :
                          'text-ie-steel'
                        }`}>
                          {lastResponse.orchestrator_metadata.query_classification.complexity.toLowerCase()}
                        </span></div>
                      )}
                      <div>Classification Confidence: <span className="font-medium">{(lastResponse.orchestrator_metadata.query_classification.confidence * 100).toFixed(0)}%</span></div>
                    </div>
                  </div>
                )}
                
                {/* Performance */}
                <div className="bg-white p-3 rounded border">
                  <h5 className="font-medium text-ie-navy mb-2">Performance</h5>
                  <div className="space-y-1 text-ie-slate">
                    <div>Total Time: <span className="font-medium">{lastResponse.orchestrator_metadata.total_execution_time?.toFixed(2)}s</span></div>
                    {lastResponse.processing_metadata && (
                      <div>Processing: <span className="font-medium">{lastResponse.processing_metadata.processing_time_seconds?.toFixed(2)}s</span></div>
                    )}
                    {lastResponse.agents_used && (
                      <div>Agents Used: <span className="font-medium">{lastResponse.agents_used}</span></div>
                    )}
                  </div>
                </div>
                
                {/* Features */}
                <div className="bg-white p-3 rounded border">
                  <h5 className="font-medium text-ie-navy mb-2">Features Used</h5>
                  <div className="space-y-1 text-ie-slate">
                    {lastResponse.processing_metadata?.intelligent_features_enabled && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-ie-cyan rounded-full"></div>
                        <span>Intelligent Mode</span>
                      </div>
                    )}
                    {executionMode === 'extended' && (
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-ie-steel rounded-full"></div>
                        <span>Extended Mode</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 bg-ie-navy rounded-full"></div>
                      <span>V2 API</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Reasoning (if available) */}
              {lastResponse.orchestrator_metadata.query_classification?.reasoning && (
                <div className="mt-3 p-3 bg-ie-mist border border-ie-sky rounded">
                  <h5 className="font-medium text-ie-navy mb-1">Analysis Reasoning</h5>
                  <p className="text-sm text-ie-navy">{lastResponse.orchestrator_metadata.query_classification.reasoning}</p>
                </div>
              )}
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="sticky bottom-0 z-10 bg-white border-t border-ie-rule px-3 py-3 sm:px-4 sm:py-4 flex-shrink-0">
        <div className="max-w-3xl mx-auto">

          
          <form onSubmit={handleSubmit} className="flex space-x-2 sm:space-x-3">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={selectedProject && activeConversation ? "Ask a question" : (!selectedProject ? "Select a project first" : "Start a new chat to begin")}
                disabled={!selectedProject || !activeConversation || isLoading}
                aria-label="Your question"
                className="w-full px-5 py-3 border border-ie-navy rounded-lg resize-none disabled:bg-ie-pale disabled:border-ie-rule disabled:cursor-not-allowed transition-colors duration-200 text-ie-navy placeholder-ie-slate overflow-y-auto"
                style={{ minHeight: '48px', maxHeight: '200px' }}
                rows={1}
              />
            </div>
            <div className="flex items-center" style={{ height: textareaHeight }}>
              <button
                type="submit"
                disabled={!inputValue.trim() || !selectedProject || !activeConversation || isLoading}
                className="px-5 sm:px-7 bg-ie-navy text-white border border-ie-navy rounded-pill hover:bg-ie-sky hover:text-ie-navy disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300 font-condensed font-medium text-sm uppercase tracking-wide flex items-center justify-center h-12"
              >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Sending</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <span>Send</span>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                  </svg>
                </div>
              )}
              </button>
            </div>
          </form>
          <div className="flex items-center justify-between mt-3">
            <p className="hidden sm:block text-xs text-ie-slate">
              Press <kbd className="px-1.5 py-0.5 bg-white border border-ie-rule rounded text-xs">Enter</kbd> to send, <kbd className="px-1.5 py-0.5 bg-white border border-ie-rule rounded text-xs">Shift + Enter</kbd> for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;